// A puzzle's grid as a small picture (Home and Library mockups): the solve screen's drawing with
// nothing filled in, at thumbnail size. A fog puzzle shows its starting fog (no spoilers), even
// though thumbnails are drawn from documents without a solution.
import type { PuzzleDoc } from '@zinc/shared';
import { memo, useMemo } from 'react';
import { emptyState } from '../lib/game';
import { buildGeometry } from '../lib/geometry';
import { Grid } from './solve/Grid';

const NONE: ReadonlySet<number> = new Set();

export const Thumbnail = memo(function Thumbnail({
  doc,
  size,
  label,
  className = '',
}: {
  doc: PuzzleDoc;
  /** Width and height in CSS pixels. */
  size: number;
  /** For screen readers; the thumbnail is decorative without it. */
  label?: string;
  className?: string;
}) {
  const geometry = useMemo(() => {
    try {
      return buildGeometry(doc);
    } catch {
      return null;
    }
  }, [doc]);
  const state = useMemo(() => (geometry ? emptyState(geometry) : null), [geometry]);
  return (
    <span
      className={`pointer-events-none flex shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-surface ${className}`}
      style={{ width: size, height: size }}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      {geometry && state && (
        <Grid
          doc={doc}
          geometry={geometry}
          state={state}
          selection={NONE}
          markStyle="sudokupad"
          startingFog
          label={label ?? ''}
        />
      )}
    </span>
  );
});
